import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { queryKeysDataSyncJob } from './queryKeys';
import { type PaginatedDataSyncJobResponse } from './validations/types';

type SetTaskActiveParams = {
  taskId: string;
  isActive: boolean;
};

type SuccessResponse = {
  id: string;
};

/**
 * The toggled task's previous `isActive` value, captured on mutate so the
 * optimistic update can be rolled back without touching other tasks.
 */
type SetTaskActiveContext = {
  previousIsActive: boolean | undefined;
};

/**
 * Returns a copy of the cached job list with a single task's `isActive` patched.
 * Scoped to one task so overlapping toggles of other tasks are not clobbered.
 */
function setTaskActiveInJobs(
  jobs: PaginatedDataSyncJobResponse | undefined,
  taskId: string,
  isActive: boolean,
): PaginatedDataSyncJobResponse | undefined {
  if (!jobs) {
    return jobs;
  }

  return {
    ...jobs,
    data: jobs.data.map((job) => ({
      ...job,
      dataSyncTasks: job.dataSyncTasks.map((task) =>
        task.id === taskId ? { ...task, isActive } : task,
      ),
    })),
  };
}

/**
 * Activates or deactivates a data sync task by updating its is_active flag.
 */
export const setDataSyncTaskActive = async ({
  taskId,
  isActive,
}: SetTaskActiveParams): Promise<SuccessResponse> => {
  // vestigasApi has case conversion enabled, so `isActive` is sent as `is_active`.
  const response = await vestigasApi
    .put(`data_sync/sync_task/${taskId}`, { json: { isActive } })
    .json<SuccessResponse>();
  return response;
};

/**
 * React Query mutation hook for toggling a data sync task's active state.
 *
 * Optimistically patches the cached job list so the card reflects the new state
 * immediately (the list is the only place this flag is rendered), and rolls back
 * just the toggled task if the request fails.
 *
 * @param options - Additional mutation options
 */
export const useMutationSetTaskActive = (
  options?: Omit<
    UseMutationOptions<SuccessResponse, Error, SetTaskActiveParams>,
    'mutationFn'
  >,
) => {
  const { t } = useTranslation('dataSync');
  const queryClient = useQueryClient();
  const { onMutate, onError, onSuccess, ...restOptions } = options ?? {};

  // oxlint-disable sort-keys -- TanStack's mutation-property-order requires onMutate before onError, which conflicts with sort-keys
  return useMutation<
    SuccessResponse,
    Error,
    SetTaskActiveParams,
    SetTaskActiveContext
  >({
    ...restOptions,
    mutationFn: setDataSyncTaskActive,
    async onMutate(variables, context) {
      // Cancel in-flight job refetches so they can't clobber the optimistic update.
      await queryClient.cancelQueries({
        queryKey: queryKeysDataSyncJob.getAll(),
      });

      // Remember only the toggled task's prior state so a rollback can revert
      // just this task without overwriting concurrent toggles of other tasks.
      const previousIsActive = queryClient
        .getQueryData<PaginatedDataSyncJobResponse>(
          queryKeysDataSyncJob.getAll(),
        )
        ?.data.flatMap((job) => job.dataSyncTasks)
        .find((task) => task.id === variables.taskId)?.isActive;

      queryClient.setQueryData<PaginatedDataSyncJobResponse>(
        queryKeysDataSyncJob.getAll(),
        (jobs) =>
          setTaskActiveInJobs(jobs, variables.taskId, variables.isActive),
      );

      await onMutate?.(variables, context);

      return { previousIsActive };
    },
    onError(error, variables, onMutateResult, context) {
      // Revert only the toggled task to its previous state.
      const previousIsActive = onMutateResult?.previousIsActive;
      if (previousIsActive !== undefined) {
        queryClient.setQueryData<PaginatedDataSyncJobResponse>(
          queryKeysDataSyncJob.getAll(),
          (jobs) =>
            setTaskActiveInJobs(jobs, variables.taskId, previousIsActive),
        );
      }

      onError?.(error, variables, onMutateResult, context);
    },
    async onSuccess(data, variables, onMutateResult, context) {
      ToastService.success(t(($) => $.toast.taskUpdated));

      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
  // oxlint-enable sort-keys
};
